import React, { FC, MutableRefObject, useMemo } from 'react';
import { FormInstance, Space, Spin } from 'antd';
import { Button, Form, InputNumber, message, Modal, PopConfirm, Select, Table } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { v4 as uuidv4 } from 'uuid';
import { isEmpty } from 'lodash-es';
import { MenuOutlined, PlusOutlined } from '@ant-design/icons';
import { DndContext, DragEndEvent, PointerSensor, UniqueIdentifier, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import DragRow from '@/pages/WorkShopOverviewConfig/components/DragRow';
import {
  apiV2AreaUpsertConfigControlPost,
  apiV2AreaGetAllMediumValveDetailPost,
} from '@maxtropy/device-customer-apis-v2';
import { getDeviceProperty } from '@/api/scada';
import { DataPropertyType } from '@/shared/types';

import { CommandStatus, ValveDevice } from '../types';
import styles from '../../index.module.scss';

interface CommandConfigModalProps {
  visible: boolean;
  areaId?: number;
  mediumId?: number;
  type: CommandStatus;
  device?: ValveDevice;
  onCancel: () => void;
}

const LEGACY_DISTRIBUTE_SUCCESS_RULE_KEYS = [
  'successAsset',
  'distributeSuccessRule',
  'distributeJudgeRule',
  'judgeDistributeSuccessRule',
  'commandSuccessRule',
] as const;

const DISTRIBUTE_SUCCESS_RULE_OPERATOR_OPTIONS = [
  { label: '>', value: 0 },
  { label: '>=', value: 1 },
  { label: '<', value: 2 },
  { label: '<=', value: 3 },
  { label: '=', value: 4 },
];

type SuccessRuleDetail = {
  dataPropertyId?: number | string;
  dataPropertyName?: string;
  type?: number;
  targetValue?: number;
  operator?: number;
  distributeTargetValue?: {
    value?: number;
    compareType?: number;
  };
};

const CommandConfigModal: FC<CommandConfigModalProps> = props => {
  const { areaId, mediumId, type, device, visible, onCancel } = props;

  const [form] = Form.useForm();
  const formRef: MutableRefObject<FormInstance<any> | null> = React.useRef(null);
  const commandControlDetail = Form.useWatch('commandControlDetail', form);
  const successRuleDataPropertyId = Form.useWatch(['distributeSuccessRule', 'dataPropertyId'], form);

  const { data: propertyList } = useRequest(() => getDeviceProperty(device?.deviceId!), {
    ready: visible && !!device?.deviceId,
    refreshDeps: [visible, device?.deviceId],
  });

  const { loading } = useRequest(
    () => apiV2AreaGetAllMediumValveDetailPost({ id: areaId, mediumId, deviceId: device!.deviceId }),
    {
      onSuccess: data => {
        const commandControlDetailKey =
          type === CommandStatus.OPEN ? 'commandControlOpenDetail' : 'commandControlCloseDetail';
        const legacySuccessRuleData = type === CommandStatus.OPEN
          ? ((data as Record<string, any>)?.openSuccessAsset as SuccessRuleDetail | undefined)
          : ((data as Record<string, any>)?.closeSuccessAsset as SuccessRuleDetail | undefined);
        const fallbackSuccessRuleData = LEGACY_DISTRIBUTE_SUCCESS_RULE_KEYS.map(
          key => (data as Record<string, any>)?.[key] as SuccessRuleDetail | undefined
        ).find(Boolean);
        const finalSuccessRuleData = legacySuccessRuleData ?? fallbackSuccessRuleData;
        const successRuleProperty = propertyList?.find(
          item => item.dataPropertyId === Number(finalSuccessRuleData?.dataPropertyId)
        );
        const isSuccessRuleEnum = successRuleProperty?.dataPropertyType === DataPropertyType.ENUM;
        const distributeSuccessRule = finalSuccessRuleData
          ? {
              dataPropertyId: Number(finalSuccessRuleData?.dataPropertyId),
              dataPropertyName: finalSuccessRuleData?.dataPropertyName,
              type: successRuleProperty?.dataPropertyType,
              distributeTargetValue: {
                value: finalSuccessRuleData?.targetValue ?? finalSuccessRuleData?.distributeTargetValue?.value,
                compareType: isSuccessRuleEnum
                  ? undefined
                  : finalSuccessRuleData?.operator ?? finalSuccessRuleData?.distributeTargetValue?.compareType,
              },
            }
          : undefined;

        form.setFieldsValue({
          commandControlDetail: data[commandControlDetailKey]?.map(item => ({
            rowKey: uuidv4(),
            ...item,
          })),
          distributeSuccessRule,
        });
      },
      ready: visible && !!device?.deviceId,
      refreshDeps: [visible, device?.deviceId, mediumId, areaId, type],
    }
  );

  // 数据属性选项
  const propertyOptions = useMemo(() => {
    return propertyList?.map(p => ({
      value: p.dataPropertyId,
      label: p.dataPropertyName,
      type: p.dataPropertyType,
    }));
  }, [propertyList]);

  const distributeSuccessRulePropertyOptions = useMemo(() => {
    return propertyList
      ?.filter(item => item.dataPropertyType === DataPropertyType.YC || item.dataPropertyType === DataPropertyType.ENUM)
      .map(item => ({
        value: item.dataPropertyId,
        label: item.dataPropertyName,
      }));
  }, [propertyList]);

  const selectedSuccessRuleProperty = useMemo(() => {
    return propertyList?.find(item => item.dataPropertyId === successRuleDataPropertyId);
  }, [propertyList, successRuleDataPropertyId]);

  const isSuccessRuleEnum = selectedSuccessRuleProperty?.dataPropertyType === DataPropertyType.ENUM;
  const isSuccessRuleYc = selectedSuccessRuleProperty?.dataPropertyType === DataPropertyType.YC;

  const distributeSuccessRuleEnumOptions = useMemo(() => {
    return (
      selectedSuccessRuleProperty?.enumValue?.map(item => ({
        value: item.value,
        label: item.name,
      })) ?? []
    );
  }, [selectedSuccessRuleProperty]);

  const handleSubmit = () => {
    form.validateFields().then(values => {
      if (isEmpty(values?.commandControlDetail)) {
        message.error('请配置控制指令');
        return;
      }
      const selectedRuleProperty = propertyList?.find(
        property => property.dataPropertyId === values?.distributeSuccessRule?.dataPropertyId
      );
      const successAsset = selectedRuleProperty
        ? {
            dataPropertyId: selectedRuleProperty.dataPropertyId,
            dataPropertyName: selectedRuleProperty.dataPropertyName,
            operator:
              selectedRuleProperty.dataPropertyType === DataPropertyType.ENUM
                ? 4
                : values?.distributeSuccessRule?.distributeTargetValue?.compareType,
            targetValue: values?.distributeSuccessRule?.distributeTargetValue?.value,
          }
        : undefined;

      const submitData = {
        id: areaId,
        mediumId,
        deviceId: device!.deviceId,
        controlType: type,
        ...values,
        successAsset,
      };
      delete (submitData as any).distributeSuccessRule;

      apiV2AreaUpsertConfigControlPost(submitData as any).then(() => {
        onCancel();
      });
    });
  };

  const columns = [
    {
      title: '',
      width: 40,
      render: (_: any, __: any, index: number) => (
        <Form.Item name={['commandControlDetail', index, 'id']} style={{ marginBottom: 0 }}>
          <MenuOutlined />
        </Form.Item>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '目标数据属性',
      render: (_: any, __: any, index: number) => (
        <Form.Item
          name={['commandControlDetail', index, 'dataPropertyId']}
          rules={[{ required: true, message: '请选择' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder="请选择数据属性"
            options={propertyOptions}
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            onChange={(value: number) => {
              const selectedProperty = propertyList?.find(p => p.dataPropertyId === value);
              // 目标数据属性变化时，清空下发目标值
              form.setFieldValue(['commandControlDetail', index, 'distributeTargetValue'], undefined);
              form.setFieldValue(['commandControlDetail', index, 'type'], selectedProperty?.dataPropertyType);
            }}
          />
        </Form.Item>
      ),
    },
    {
      title: '下发目标值',
      render: (_: any, __: any, index: number) => (
        <Form.Item noStyle dependencies={[['commandControlDetail', index, 'dataPropertyId']]}>
          {() => {
            const dataPropertyId = form.getFieldValue(['commandControlDetail', index, 'dataPropertyId']);
            const selectedProperty = propertyList?.find(p => p.dataPropertyId === dataPropertyId);
            const isEnum = selectedProperty?.dataPropertyType === DataPropertyType.ENUM;

            // 校验是否重复
            const validateUnique = (_: any, value: any) => {
              if (!value || !dataPropertyId) return Promise.resolve();
              const allParams = form.getFieldValue('commandControlDetail') || [];
              const isDuplicate = allParams.some((item: any, idx: number) => {
                if (idx === index) return false; // 排除当前行
                return item.dataPropertyId === dataPropertyId && item.distributeTargetValue?.value === value;
              });
              if (isDuplicate) {
                return Promise.reject(new Error('目标数据属性和下发目标值不能重复'));
              }
              return Promise.resolve();
            };

            // 枚举类型：使用 Select
            if (isEnum) {
              const enumOptions =
                selectedProperty?.enumValue?.map(e => ({
                  value: e.value,
                  label: e.name,
                })) || [];

              return (
                <Form.Item
                  name={['commandControlDetail', index, 'distributeTargetValue', 'value']}
                  rules={[{ required: true, message: '请选择' }, { validator: validateUnique }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder="请选择" options={enumOptions} />
                </Form.Item>
              );
            }

            // 遥测值类型：使用 InputNumber
            return (
              <Form.Item
                name={['commandControlDetail', index, 'distributeTargetValue', 'value']}
                rules={[{ required: true, message: '请输入' }, { validator: validateUnique }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber placeholder="请输入" style={{ width: '100%' }} />
              </Form.Item>
            );
          }}
        </Form.Item>
      ),
    },
    {
      title: '下发间隔时间',
      width: 160,
      render: (_: any, __: any, index: number) => (
        <Form.Item
          name={['commandControlDetail', index, 'millSeconds']}
          rules={[{ required: true, message: '请输入' }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber suffix="s" style={{ width: '100%' }} />
        </Form.Item>
      ),
    },
    {
      title: '操作',
      width: 60,
      render: (_: any, record: any, index: number) => (
        <PopConfirm title="你确定要删除吗？" onConfirm={() => deleteControlCommand(record.rowKey)}>
          <Button type="link">删除</Button>
        </PopConfirm>
      ),
    },
  ];

  // 删除控制指令
  const deleteControlCommand = (rowKey: string) => {
    const prevList = form.getFieldValue('commandControlDetail');
    const removeId = prevList.findIndex((item: any) => {
      return item.rowKey === rowKey;
    });
    prevList.splice(removeId, 1);
    form.setFieldsValue({ commandControlDetail: prevList });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  // 拖拽事件
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = commandControlDetail.findIndex((i: { rowKey: UniqueIdentifier }) => i.rowKey === active.id);
      const overIndex = commandControlDetail.findIndex(
        (i: { rowKey: UniqueIdentifier | undefined }) => i.rowKey === over?.id
      );
      form.setFieldsValue({
        commandControlDetail: arrayMove(commandControlDetail, activeIndex, overIndex),
      });
    }
  };

  // 新增控制指令
  const addControlCommandRow = () => {
    const newRow = {
      rowKey: uuidv4(),
      dataPropertyId: undefined,
    };

    const prevList = form.getFieldValue('commandControlDetail') || [];
    prevList.push(newRow);
    form.setFieldValue('commandControlDetail', [...prevList]);
  };

  const handleDistributeSuccessRulePropertyChange = (value?: number) => {
    const selectedProperty = propertyList?.find(item => item.dataPropertyId === value);
    form.setFieldValue(['distributeSuccessRule', 'distributeTargetValue'], undefined);
    form.setFieldValue(['distributeSuccessRule', 'type'], selectedProperty?.dataPropertyType);
    form.setFieldValue(['distributeSuccessRule', 'dataPropertyName'], selectedProperty?.dataPropertyName);
  };

  return (
    <Modal open={visible} title="配置" size="large" destroyOnClose onCancel={onCancel} onOk={handleSubmit} bodyScroll>
      <Spin spinning={loading}>
        <Form form={form} ref={formRef} layout="horizontal">
          <Form.Item label="判断下发成功规则" className={styles.successRuleItem}>
            <Space size={8} align="center" className={styles.successRuleRow}>
              <Form.Item name={['distributeSuccessRule', 'dataPropertyId']} style={{ marginBottom: 0, width: 180 }}>
                <Select
                  allowClear
                  placeholder="请选择数据属性"
                  options={distributeSuccessRulePropertyOptions}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onChange={handleDistributeSuccessRulePropertyChange}
                />
              </Form.Item>

              {isSuccessRuleYc && (
                <>
                  <Form.Item
                    name={['distributeSuccessRule', 'distributeTargetValue', 'compareType']}
                    style={{ marginBottom: 0, width: 100 }}
                    rules={[{ required: true, message: '请选择' }]}
                  >
                    <Select placeholder="请选择" options={DISTRIBUTE_SUCCESS_RULE_OPERATOR_OPTIONS} />
                  </Form.Item>
                  <Form.Item
                    name={['distributeSuccessRule', 'distributeTargetValue', 'value']}
                    style={{ marginBottom: 0, width: 160 }}
                    rules={[{ required: true, message: '请输入' }]}
                  >
                    <InputNumber style={{ width: '100%' }} placeholder="请输入" />
                  </Form.Item>
                </>
              )}

              {isSuccessRuleEnum && (
                <Form.Item
                  name={['distributeSuccessRule', 'distributeTargetValue', 'value']}
                  style={{ marginBottom: 0, width: 180 }}
                  rules={[{ required: true, message: '请选择' }]}
                >
                  <Select placeholder="请选择枚举值" options={distributeSuccessRuleEnumOptions} />
                </Form.Item>
              )}
            </Space>
          </Form.Item>

          <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext
              items={(commandControlDetail ?? []).map((i: any) => i?.rowKey)}
              strategy={verticalListSortingStrategy}
            >
              <Form.Item name="commandControlDetail" valuePropName="dataSource" className={styles.tableBox}>
                <Table
                  components={{
                    body: {
                      row: DragRow,
                    },
                  }}
                  rowKey="rowKey"
                  columns={columns}
                />
              </Form.Item>
            </SortableContext>
          </DndContext>

          <Button
            type="dashed"
            ghost
            icon={<PlusOutlined />}
            wrapClassName={styles.addRowBtn}
            style={{ width: '100%' }}
            onClick={() => {
              addControlCommandRow();
            }}
          >
            新增控制指令
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CommandConfigModal;
